import React, { type ReactNode } from 'react';
// eslint-disable-next-line @atlaskit/ui-styling-standard/use-compiled -- Ignored via go/DSP-18766
import styled, { css } from 'styled-components';
import ChevronRightIcon from '@atlaskit/icon/utility/migration/chevron-right';
import ChevronRightLargeIcon from '@atlaskit/icon/utility/migration/chevron-right--chevron-right-large';
import { token } from '@atlaskit/tokens';

export type TriggerTextVariant = 'regular' | 'large';

type Props = {
	children: ReactNode;
	label: ReactNode;
	/**
	 * Wrapper is <p> tag so only phrasing content is allowed.
	 * If passing <a> tag it should also only contain phrasing content
	 * */
	description?: ReactNode;
	initialOpen?: boolean;
	/** Default variant is `regular` */
	variant?: TriggerTextVariant;
};

export const ExpandableSection = ({
	children,
	label,
	description,
	initialOpen,
	variant = 'regular',
}: Props) => (
	<ExpandableSectionContainer open={initialOpen}>
		<Trigger>
			<TriggerText>{label}</TriggerText>
			<TriggerIconContainer>
				{variant === 'regular' && (
					<ChevronRightIcon spacing="spacious" label="" color={token('color.text')} />
				)}
				{variant === 'large' && (
					<ChevronRightLargeIcon spacing="spacious" label="" color={token('color.text')} />
				)}
			</TriggerIconContainer>
		</Trigger>
		<HiddenContentContainer hasDescription={Boolean(description)}>
			{description && <Description>{description}</Description>}
			{children}
		</HiddenContentContainer>
	</ExpandableSectionContainer>
);

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const TriggerIconContainer = styled.span({
	display: 'inline-flex',
	transition: 'transform 250ms cubic-bezier(0.15, 1, 0.3, 1)',
});

// TODO: migrate to object syntax. Autofix is available for many cases. Remove the eslint-disable for @atlaskit/design-system/no-styled-tagged-template-expression to check.
// eslint-disable-next-line @atlaskit/design-system/no-styled-tagged-template-expression, @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const ExpandableSectionContainer = styled.details`
	&[open] {
		${TriggerIconContainer} {
			transform: rotate(90deg);
		}
	}
`;

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const Trigger = styled.summary({
	width: '100%',
	display: 'inline-flex',
	justifyContent: 'space-between',
	alignItems: 'center',
	padding: `${token('space.050')} 0`,
	/** Copied from Button component version ^16.3.5 */
	transition: '0.1s ease-out 0s',
	transitionProperty: 'background-color, margin-left, padding-left',
	'&:hover': {
		cursor: 'pointer',
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
		backgroundColor: token('color.background.neutral.hovered'),
		borderRadius: '3px',
		marginLeft: token('space.negative.075'),
		paddingLeft: token('space.075'),
	},
});

// eslint-disable-next-line @atlaskit/design-system/no-styled-tagged-template-expression, @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const TriggerText = styled.span`
	font: ${token('font.heading.xxsmall')};
	color: ${token('color.text.subtle')};
	margin-top: 0;
`;

// eslint-disable-next-line @atlaskit/design-system/no-styled-tagged-template-expression, @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const Description = styled.p`
	font: ${token('font.body.small')};

	color: ${token('color.text.subtle')};
	margin-top: 0;
	margin-bottom: ${token('space.200')};
`;

// TODO: migrate to object syntax. Autofix is available for many cases. Remove the eslint-disable for @atlaskit/design-system/no-styled-tagged-template-expression to check.
// eslint-disable-next-line @atlaskit/design-system/no-styled-tagged-template-expression, @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const HiddenContentContainer = styled.div<{ hasDescription: boolean }>`
	${({ hasDescription }) =>
		!hasDescription &&
		css({
			marginTop: token('space.050'),
		})}
`;
